import { useForm } from '@mantine/form'
import { showErrorNotification } from '@v2/domains/notification/notification.hook'
import { useEffect, useState } from 'react'

import { ICreateUserRequest, UserType } from '../user.types'
import { getUserFormValidation } from '../utils/user.validations'
import { useUser } from './useUser.hook'
import { useUserMutations } from './useUserMutations.hook'

export const useUserEdit = (userId: string) => {
  const [editMode, setEditMode] = useState(false)

  const { user, isLoading: isLoadingUser, isSuccess } = useUser(userId)

  const { updateUser, isUpdating } = useUserMutations()

  const userForm = useForm<ICreateUserRequest>({
    mode: 'uncontrolled',
    initialValues: {
      id: '',
      username: '',
      firstName: '',
      lastName: '',
      phone: '',
      aircallUserId: '',
      password: '',
      organizationId: '',
      type: user?.type || UserType.REGULAR,
    },
    validateInputOnBlur: true,
    validate: getUserFormValidation(),
    transformValues: (values) => {
      const { phone, ...transformed } = values
      return {
        ...transformed,
        ...(!!phone ? { phone } : {}),
      }
    },
  })

  const membershipData = user?.membership || {
    id: '',
    role: '',
    roleId: '',
    hasAPIKey: false,
    status: 'inactive',
  }

  const emailDomains = user?.emailDomains

  const resetFormInitialUserValues = () => {
    userForm.setValues({
      id: user?.id ?? '',
      username: user?.username ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phone: user?.phone ?? '',
      aircallUserId: user?.aircallUserId ?? '',
      // hipaaCertified: user?.hipaaCertified ?? false,
      password: '',
      organizationId: '',
      type: user?.type ?? UserType.REGULAR,
    })
  }

  useEffect(() => {
    resetFormInitialUserValues()
  }, [user, isSuccess])

  const handleSubmit = () => {
    userForm.onSubmit(async (values) => {
      if (!values.id) {
        showErrorNotification('User ID is missing')
        return
      }
      await updateUser(values)
      setEditMode(false)
    })()
  }

  const handleCancel = () => {
    resetFormInitialUserValues()
    setEditMode(false)
  }

  return {
    editMode,
    isLoadingUser,
    isLoadingEdit: isUpdating,
    membershipData,
    emailDomains,
    userForm,
    setEditMode,
    submitForm: handleSubmit,
    cancelEdit: handleCancel,
  }
}
